import React, { useEffect, useState } from "react";
import { Eye, RefreshCw, Send, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmt = (iso) => { const d = new Date(iso || ""); return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); };

// Admin Enquiries header strip: preview today's 08:00 campaign digest + see/run the 24h/48h campaign follow-ups.
export default function CampaignToolsPanel({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [html, setHtml] = useState("");
  const [fu, setFu] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadFollowups = () => api.get("/admin/campaign-followups/preview").then(({ data }) => setFu(data)).catch(() => setFu(null));
  useEffect(() => { loadFollowups(); }, []);
  useEffect(() => {
    if (!open) return;
    api.get("/admin/campaign-digest/preview", { responseType: "text" }).then(({ data }) => setHtml(data)).catch(() => toast.error("Could not render the campaign digest"));
  }, [open]);

  const runNow = async () => {
    const n = (fu?.supplier_reminders?.length || 0) + (fu?.buyer_nudges?.length || 0);
    if (!n || !window.confirm(`Send ${fu.supplier_reminders.length} supplier reminder(s) and ${fu.buyer_nudges.length} buyer nudge(s) now?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/campaign-followups/send");
      toast.success(`Sent · suppliers ${data.supplier_reminders.sent} · buyers ${data.buyer_nudges.sent}`);
      loadFollowups(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Run failed"); }
    finally { setBusy(false); }
  };

  const sup = fu?.supplier_reminders || [];
  const buy = fu?.buyer_nudges || [];
  return (
    <div className="mb-4 bg-[#11211A] text-white px-5 py-4 flex flex-wrap items-center gap-4" data-testid="campaign-tools-panel">
      <div className="flex-1 min-w-[260px]">
        <div className="tracking-overline text-[10px] text-[#F0C866]">/get-quotes campaign tools</div>
        <div className="text-xs text-white/70 mt-1" data-testid="campaign-followups-summary">
          {fu
            ? <>Next hourly follow-up run: <strong className="text-white">{sup.length}</strong> supplier reminder{sup.length === 1 ? "" : "s"} ({fu.supplier_after_hours}h unreplied) · <strong className="text-white">{buy.length}</strong> buyer nudge{buy.length === 1 ? "" : "s"} ({fu.buyer_after_hours}h no reply). Sends only while automated sweeps are on.</>
            : "Loading follow-up queue…"}
        </div>
        {(sup.length > 0 || buy.length > 0) && (
          <div className="mt-2 text-[11px] text-white/80 space-y-0.5" data-testid="campaign-followups-list">
            {sup.map((r) => <div key={r.rfq_id}><Clock className="w-3 h-3 inline -mt-0.5 mr-1 text-[#F0C866]" />Supplier · {r.supplier} ← {r.buyer} · {r.product} · {fmt(r.created_at)}</div>)}
            {buy.map((b) => <div key={b.rfq_id}><Clock className="w-3 h-3 inline -mt-0.5 mr-1 text-[#F0C866]" />Buyer · {b.buyer} ({b.buyer_email}) · {b.product} · no reply from {b.suppliers.join(", ")}</div>)}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button type="button" onClick={loadFollowups} className="text-white/70 hover:text-white" title="Refresh queue" data-testid="campaign-followups-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={runNow} disabled={busy || (!sup.length && !buy.length)} className="text-[10px] uppercase tracking-widest font-semibold border border-white/40 px-3 py-1.5 hover:border-white disabled:opacity-40" data-testid="campaign-followups-send"><Send className="w-3 h-3 inline -mt-0.5 mr-1" />{busy ? "Sending…" : "Run follow-ups now"}</button>
        <button type="button" onClick={() => setOpen(true)} className="text-[10px] uppercase tracking-widest font-semibold bg-[#F0C866] text-[#11211A] px-3 py-1.5 hover:bg-[#F6D98A]" data-testid="campaign-digest-preview-button"><Eye className="w-3 h-3 inline -mt-0.5 mr-1" />Preview today's campaign digest</button>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 bg-[#11211A]/60 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="campaign-digest-preview-modal">
          <div className="bg-white text-[#11211A] w-full max-w-3xl shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E8ECE9]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#7D8C85]">/get-quotes campaign digest</div>
                <div className="text-sm">Exactly what hello@ receives at 08:00 AEST, rendered live from today's data. Nothing is sent from here.</div>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost text-xs" data-testid="campaign-digest-preview-close">Close</button>
            </div>
            <iframe title="Campaign digest preview" srcDoc={html} sandbox="" className="w-full h-[75vh] bg-white" data-testid="campaign-digest-preview-frame" />
          </div>
        </div>
      )}
    </div>
  );
}
